import axios from 'axios';
import { useContext, useEffect, useState } from 'react';
import { Button, Spinner, Table } from 'react-bootstrap';
import { FaCheck, FaEye, FaEraser } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import SuccessModal from '../components/modals/SuccessModal';
import ConfirmModal from '../components/modals/ConfirmModal';
import { useNotificationModal } from '../hooks/useNotificationModal';

import '../styles/ApprovePO.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function ApprovePO() {
  const { userData } = useContext(AuthContext);

  const {
    showSuccessModal,
    successMessage,
    successTitle,
    showErrorModal,
    errorMessage,
    errorTitle,
    showSuccess,
    showError,
    hideSuccess,
    hideError
  } = useNotificationModal();

  const [, setPurchaseOrders] = useState([]);
  const [allPurchaseOrders, setAllPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState('');
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedPO, setSelectedPO] = useState('');
  const initialFilter = {
    po_number: '',
    supplier_name: '',
    total_quantity: '',
    total_styles: '',
    total_cost: '',
    created_at: '',
    status: 'Pending'
  };
  const [filter, setFilter] = useState(initialFilter);
  const [debouncedFilter, setDebouncedFilter] = useState(filter);
  const navigate = useNavigate();

  const fetchPOs = async (filters = null) => {
    setLoading(true);
    try {
       const usedFilters = filters || filter;
       const params = {
         company_code: userData?.company_code,
         po_type: "direct",
         ...usedFilters,
       };

      const res = await axios.get(`${BASE_URL}/api/admin/po/get-purchase-orders`, { params });
      const rows = res.data.purchase_orders || [];
      setAllPurchaseOrders(rows);
      setPurchaseOrders(rows);
    } catch (err) {
      showError(err.response?.data?.message || err.message, 'Error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilter(filter), 300);
    return () => clearTimeout(t);
  }, [filter]);

  useEffect(() => {
    if (userData?.company_code) fetchPOs(debouncedFilter);
    // eslint-disable-next-line
  }, [userData?.company_code, debouncedFilter]);

  const openApproveModal = (po_number) => {
    setSelectedPO(po_number);
    setShowApproveModal(true);
  };

  const closeApproveModal = () => {
    setShowApproveModal(false);
    setSelectedPO('');
  };

  const confirmApprove = async () => {
    const po_number = selectedPO;
    if (!po_number) return;
    setApproving(po_number);
    try {
      await axios.put(`${BASE_URL}/api/admin/po/approve/${po_number}`);
      showSuccess(`Purchase Order ${po_number} approved successfully!`, 'Success');
      fetchPOs(debouncedFilter);
      closeApproveModal();
    } catch (err) {
      showError(err.response?.data?.message || err.message, 'Error');
    } finally {
      setApproving('');
    }
  };

  const handleView = (po_number) => {
    navigate(`/merchandising/po/${po_number}/view`, { state: { from: '/approve-po' } });
  }

  const clearFilters = () => {
    setFilter(initialFilter);
    setDebouncedFilter(initialFilter);
    // fetch immediately so UI updates without waiting for debounce
    fetchPOs(initialFilter);
  };

  return (
    <div className="fluid-container">
      <div className="style-header">
        <h1> Approve Purchase Orders </h1>
      </div>
      <div className="mb-3">
        <Button
          variant={filter.status === 'Pending' ? 'primary' : 'outline-primary'}
          className="me-2"
          onClick={() => setFilter({ ...filter, status: 'Pending' })}
        >
          Pending
        </Button>
        <Button
          variant={filter.status === 'Approved' ? 'success' : 'outline-success'}
          className="me-2"
          onClick={() => setFilter({ ...filter, status: 'Approved' })}
        >
          Approved
        </Button>
      </div>
      {loading ? (
        <div className="text-center my-4"><Spinner animation="border" /></div>
      ) : (
        <div className="approve-table-container">
          <div className="approve-table-wrapper">
            <Table striped bordered hover className='approve-table'>
              <thead>
                <tr>
                  <th>PO Number</th>
                  <th>Supplier</th>
                  <th>Total Quantity</th>
                  <th>Total Products</th>
                  <th>Total Cost</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr className="filter-row">
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="PO Number"
                      value={filter.po_number}
                      onChange={e => setFilter({ ...filter, po_number: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Supplier"
                      value={filter.supplier_name}
                      onChange={e => setFilter({ ...filter, supplier_name: e.target.value })}
                    />
                  </td>
                  <td className="number-col">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Qty"
                      value={filter.total_quantity}
                      onChange={e => setFilter({ ...filter, total_quantity: e.target.value })}
                    />
                  </td>
                  <td className="number-col">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Products"
                      value={filter.total_styles}
                      onChange={e => setFilter({ ...filter, total_styles: e.target.value })}
                    />
                  </td>
                  <td className="number-col">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Cost"
                      value={filter.total_cost}
                      onChange={e => setFilter({ ...filter, total_cost: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={filter.created_at}
                      onChange={e => setFilter({ ...filter, created_at: e.target.value })}
                    />
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Button
                        variant="outline"
                        className="pf-clear-filters-btn"
                        onClick={(e) => { e.stopPropagation(); clearFilters(); }}
                        title="Clear filters"
                      >
                        <FaEraser />
                      </Button>
                    </div>
                  </td>
                </tr>

                {(() => {
                  const f = debouncedFilter;
                  const filtered = (allPurchaseOrders || []).filter(po => {
                    if (f.po_number && !String(po.po_number || '').toLowerCase().includes(String(f.po_number).toLowerCase())) return false;
                    if (f.supplier_name && !String(po.supplier_name || '').toLowerCase().includes(String(f.supplier_name).toLowerCase())) return false;
                    if (f.total_quantity && !String(po.total_quantity || '').includes(String(f.total_quantity))) return false;
                    if (f.total_styles && !String(po.total_styles || '').includes(String(f.total_styles))) return false;
                    if (f.total_cost && !String(po.total_cost || '').includes(String(f.total_cost))) return false;
                    if (f.status && String(f.status).trim() !== '') {
                      if (String(po.status || '').toLowerCase() !== String(f.status).toLowerCase()) return false;
                    }
                    if (f.created_at && po.created_at) {
                      const poDate = new Date(po.created_at).toISOString().split('T')[0];
                      if (poDate !== f.created_at) return false;
                    }
                    return true;
                  });

                  if (filtered.length === 0) {
                    return (
                      <tr>
                        <td colSpan={8} className="text-center">No matching purchase orders</td>
                      </tr>
                    );
                  }

                  return filtered.map((po) => (
                    <tr key={po.po_number}>
                      <td>{po.po_number}</td>
                      <td>{po.supplier_name}</td>
                      <td className="number-col">{po.total_quantity}</td>
                      <td className="number-col">{po.total_styles}</td>
                      <td className="number-col">{po.total_cost}</td>
                      <td>{new Date(po.created_at).toLocaleDateString()}</td>
                      <td>
                        {po.status === "Pending" ? (
                          <Button
                            variant="success"
                            size="sm"
                            className="me-2 app-btn"
                            onClick={() => openApproveModal(po.po_number)}
                            disabled={approving === po.po_number}
                          >
                            {approving === po.po_number ? (
                              <Spinner size="sm" />
                            ) : (
                              <FaCheck />
                            )}
                          </Button>
                        ) : (
                          <p> </p>
                        )}
                        <FaEye
                          onClick={() => handleView(po.po_number)}
                          className="action-icon me-2 text-success"
                          style={{ cursor: "pointer" }}
                          size={16}
                        />
                      </td>
                    </tr>
                  ));
                })()}
              </tbody>
            </Table>
          </div>
        </div>
      )}
      <ConfirmModal
        show={showApproveModal}
        onHide={closeApproveModal}
        onConfirm={confirmApprove}
        title="Approve Purchase Order"
        message={<>Are you sure you want to approve PO <strong>{selectedPO}</strong>?</>}
        confirmText="Approve"
        cancelText="Cancel"
        confirmVariant="success"
        loading={approving === selectedPO}
      />

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onHide={hideSuccess}
        message={successMessage}
        title={successTitle}
        variant="success"
      />

      {/* Error Modal */}
      <SuccessModal
        show={showErrorModal}
        onHide={hideError}
        message={errorMessage}
        title={errorTitle}
        variant="error"
      />
    </div>
  );
}
